import { getHomepage } from "utils";
import {
  Button,
  ConfirmationModal,
  Dropdown,
  Icon,
  TranslateWrapper,
} from "components";
import { UncontrolledTooltip } from "reactstrap";
import React, { memo, useCallback } from "react";
import NiceModal from "@ebay/nice-modal-react";
import { ProjectOrFolderDownloadModal } from "components/_Modals/ProjectDownloadModal/ProjectOrFolderDownloadModal";
import { ShareProjectModal } from "components/_Modals/";
import { MultipleProjectsDeleteMut, TroxnetUploadXmlMut } from "api/mutations";
import { toast } from "react-toastify";
import * as Scrivito from "scrivito";
import { useMutation } from "urql";
import { XMLParser } from "fast-xml-parser";
import { UploadXmlModal } from "components/_Modals/UploadXmlModal";
import { useTranslate } from "hooks";
import { useProject, useProjectActions } from "stores/project";
import { useLanguage } from "stores/globalStore";
import type { ProductListRefValue } from "interfaces/product/product.types";

type ProjectHeaderProps = {
  page: Scrivito.Obj;
  productListRef: React.RefObject<ProductListRefValue | null> | null;
};

export const ProjectHeader = memo(
  ({ page, productListRef = null }: ProjectHeaderProps) => (
    <ProjectHeaderComponent page={page} productListRef={productListRef} />
  ),
);

const ProjectHeaderComponent = Scrivito.connect(
  ({ page, productListRef }: ProjectHeaderProps) => {
    const translate = useTranslate();
    const { refetch: refetchProject, clear: clearProject } =
      useProjectActions();
    const project = useProject();
    const language = useLanguage();
    const redirectAfterDelete =
      page.get("redirectAfterDelete") || getHomepage();

    const [_uploadXML, exeUploadXml] = useMutation(TroxnetUploadXmlMut);

    const handleUploadTroxNetCom = async (
      lang: string,
      xmlContent: string | null,
      setLoader: (value: boolean) => void,
    ) => {
      if (!xmlContent) {
        return;
      }
      const parser = new XMLParser();
      const json = parser.parse(xmlContent);
      const variables = {
        input: { projectId: project?.id ?? "", data: json, languageCode: lang },
      };

      setLoader(true);
      const { data, error } = await exeUploadXml(variables);
      const errorMessage =
        error?.message || data?.uploadTroxNetComWizard?.status !== "SUCCESS"
          ? data?.uploadTroxNetComWizard?.message
          : null;
      if (errorMessage) {
        toast.error(errorMessage);
      } else {
        if (productListRef?.current) {
          for (const key of Object.keys(productListRef.current) as Array<
            keyof ProductListRefValue
          >) {
            productListRef.current[key]?.();
          }
        }
        refetchProject();
      }

      setLoader(false);
    };

    const openModal = () => {
      NiceModal.show(UploadXmlModal, {
        uploadTroxnetXml: handleUploadTroxNetCom,
      });
    };

    const handleDelete = useCallback(() => {
      NiceModal.show(ConfirmationModal, {
        submitBtnText: translate("PROJECT_MODAL_DELETE_TEXT"),
        title: translate("PROJECT_MODAL_DELETE_TEXT"),
        alertText: translate(
          "PROJECT_MODAL_DELETE_WARNING_TEXT_WITH_TITLE",
          project?.title,
        ),
        mutation: MultipleProjectsDeleteMut,
        params: { input: { ids: [project?.id], languageCode: language } },
      }).then(() => {
        const waitingBeforeRedirect = toast.loading(
          translate("IS_LOADING_TEXT"),
        );

        setTimeout(() => {
          toast.dismiss(waitingBeforeRedirect);
          Scrivito.navigateTo(redirectAfterDelete as Scrivito.Obj);

          // Delay reset so the old useEffect cannot run in between
          setTimeout(() => {
            clearProject();
          }, 500);
        }, 2000);
      });
    }, [
      translate,
      project?.title,
      project?.id,
      language,
      redirectAfterDelete,
      clearProject,
    ]);

    const openDownloadModal = useCallback(() => {
      NiceModal.show(ProjectOrFolderDownloadModal, {
        modalSubtitle: project?.title ?? "",
        projectId: project?.id,
      });
    }, [project?.id, project?.title]);

    const toggleShareModal = useCallback(() => {
      NiceModal.show(ShareProjectModal, {
        project: {
          title: project?.title ?? "",
          id: project?.id ?? "",
        },
      }).then();
    }, [project]);
    return (
      <div className="d-flex align-items-center">
        <h3
          className="text-uppercase mb-0 me-3"
          style={{ wordBreak: "break-all", overflowWrap: "anywhere" }}
        >
          {project?.title}
        </h3>
        <div className="d-flex align-items-center">
          <Button
            data-testid="ProjectItemComponent_share-btn"
            color="transparent"
            className="px-2 py-0 lh-0 border-end shadow-none"
            id="project_item_share_icon"
            onClick={toggleShareModal}
          >
            <Icon iconName="share-nodes-regular" className="icon-20" />
          </Button>
          <UncontrolledTooltip target="project_item_share_icon" placement="top">
            {translate("SHARE")}
          </UncontrolledTooltip>
          {typeof translate("SHARE") === "object" && (
            <TranslateWrapper
              tooltipText="SHARE"
              tooltipId="project_item_share_icon"
            />
          )}
          <Button
            data-testid="ProjectItemComponent_download-btn"
            color="transparent"
            className="px-2 py-0 lh-0 border-end shadow-none"
            onClick={openDownloadModal}
            id="project_item_download_icon"
          >
            <Icon iconName="arrow-down-bracket-regular" className="icon-20" />
          </Button>
          <UncontrolledTooltip
            target="project_item_download_icon"
            placement="top"
          >
            {translate("DOWNLOAD")}
          </UncontrolledTooltip>
          {typeof translate("DOWNLOAD") === "object" && (
            <TranslateWrapper
              tooltipText="DOWNLOAD"
              tooltipId="project_item_download_icon"
            />
          )}

          <Dropdown
            btnChildren={
              <Icon
                iconName="ellipsis-vertical-regular"
                className="icon-20"
                data-testid="ProjectItemComponent_more-actions-btn"
              />
            }
            btnId="project_item_more_actions_icon"
            options={[
              {
                id: "delete",
                title: translate("DELETE"),
                onClick: handleDelete,
              },
              {
                id: "uploadTroxNetCom",
                title: "Upload TROXNETCOM",
                onClick: openModal,
              },
            ]}
          />
          <UncontrolledTooltip
            target="project_item_more_actions_icon"
            placement="top"
          >
            {translate("MORE_ACTIONS")}
          </UncontrolledTooltip>
          {typeof translate("MORE_ACTIONS") === "object" && (
            <TranslateWrapper
              tooltipText="MORE_ACTIONS"
              tooltipId="project_item_more_actions_icon"
              placement="top"
            />
          )}
        </div>
      </div>
    );
  },
);
